'use client';

import dynamic from 'next/dynamic';

import billingConfig from '~/config/billing.config';

const EmbeddedCheckout = dynamic(
  async () => {
    const { EmbeddedCheckout } = await import('@kit/billing-gateway/checkout');
    return { default: EmbeddedCheckout };
  },
  { ssr: false },
);

interface BillingCheckoutProps {
  checkoutToken: string;
  onClose: () => void;
  /**
   * 'overlay' (default) — provider renders its own modal-style overlay
   *   centered on the viewport with a dark backdrop. Suitable for
   *   /account/billing and /pricing where the iframe sits on top of an
   *   existing page.
   *
   * 'inline' — provider renders just the iframe; the caller wraps it in
   *   its own container. Used by BuyCoinsModal which keeps its Dialog
   *   open and swaps the coin-pack grid for the checkout iframe.
   *
   * Only centrobill currently respects this prop; other providers
   * default to their own overlay.
   */
  variant?: 'overlay' | 'inline';
}

/**
 * Provider-aware checkout overlay/redirect.
 *
 * Reads the active billing provider from `billingConfig` and forwards the
 * `checkoutToken` to the gateway's <EmbeddedCheckout>, which dispatches to the
 * right per-provider implementation (Stripe / Lemon / Paddle overlay,
 * CentroBill HPP iframe).
 */
export function BillingCheckout({
  checkoutToken,
  onClose,
  variant,
}: BillingCheckoutProps) {
  return (
    <EmbeddedCheckout
      checkoutToken={checkoutToken}
      provider={billingConfig.provider}
      onClose={onClose}
      variant={variant}
    />
  );
}
